import React from 'react';
import { useFetcher, useNavigate } from 'react-router-dom';
import type { ActionResult } from '@oysterjs/core/graphql/actions';
import { Button } from '@oysterjs/ui/button';

import {
  Action,
  type CreateEmailTemplateMutation,
  type EmailTemplateEditorFieldsFragment,
  Resource
} from '~/api/graphql';
import { FeatureDocCallout } from '~/components/feature-doc-callout';
import { FormError } from '~/components/form-error';
import { usePermissions } from '~/components/permissions';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { actionFailureMessage } from './action-failure';
import { emailTemplatePath } from './email-template-paths';
import { nextCopyName } from './use-email-template-actions';

const whoCanChange = (template: EmailTemplateEditorFieldsFragment) =>
  template.createdBy
    ? `Only ${template.createdBy.name} or an admin can change this template.`
    : 'Only an admin can change this template.';

/** Says who can change the template, and offers someone who can make templates a copy. */
export const EmailTemplateReadOnlyNotice = ({
  template
}: {
  template: EmailTemplateEditorFieldsFragment;
}) => {
  const canCreate = usePermissions().hasPermission(Resource.EmailTemplate, Action.Create);
  const navigate = useNavigate();
  const fetcher = useFetcher<ActionResult<CreateEmailTemplateMutation>>();
  const canChooseVisibility = useVisibilityEntitlement();
  const isDuplicating = fetcher.state !== 'idle';
  const copyId = isDuplicating ? undefined : fetcher.data?.data?.createEmailTemplate.id;

  React.useEffect(() => {
    if (copyId) navigate(emailTemplatePath(copyId));
  }, [copyId]);

  // Another copy of the same name is rare enough that the server's refusal explains it.
  const duplicate = () =>
    fetcher.submit(
      { copyName: nextCopyName(template.name, []), canChooseVisibility },
      {
        method: 'post',
        encType: 'application/json',
        action: `${emailTemplatePath(template.id)}/duplicate`
      }
    );

  return (
    <div className="space-y-2">
      <FeatureDocCallout
        title={whoCanChange(template)}
        description={
          canCreate
            ? 'Duplicate it to make your own version.'
            : 'You can use this template when you write an email with Cara.'
        }
        action={
          canCreate && (
            <Button
              color="white"
              className="shrink-0 self-start"
              loading={isDuplicating}
              onClick={duplicate}
            >
              Duplicate
            </Button>
          )
        }
      />
      <FormError message={isDuplicating ? null : actionFailureMessage(fetcher.data)} />
    </div>
  );
};
